import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Search, Eye, Phone, MessageCircle, Globe, Calendar, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Table, TableBody, TableCell, TableHead, 
  TableHeader, TableRow 
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  getBusinesses, createBusiness, updateBusiness, deleteBusiness,
  getCities, getCategories, getPackages 
} from '@/lib/api';
import { toast } from 'sonner';

const statusOptions = [
  { value: 'draft', label: 'Taslak', color: 'bg-gray-500' },
  { value: 'pending_payment', label: 'Ödeme Bekliyor', color: 'bg-yellow-500' },
  { value: 'active', label: 'Aktif', color: 'bg-green-500' },
  { value: 'suspended', label: 'Askıda', color: 'bg-red-500' },
  { value: 'expired', label: 'Süresi Dolmuş', color: 'bg-gray-500' },
];

const paymentMethods = [
  { value: 'cash', label: 'Nakit' },
  { value: 'card', label: 'Kredi Kartı' },
  { value: 'transfer', label: 'Havale/EFT' },
];

export default function AdminListings() {
  const [businesses, setBusinesses] = useState([]);
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    city_id: '',
    category_id: '',
    district: '',
    address: '',
    phone: '',
    whatsapp: '',
    website: '',
    email: '',
    logo_url: '',
    cover_image_url: '',
    gallery: [],
    package_id: '',
    start_date: '',
    end_date: '',
    status: 'draft',
    payment_method: '',
    collected_by: '',
    payment_notes: '',
    seo_title: '',
    seo_description: ''
  });
  const [galleryText, setGalleryText] = useState('');

  const fetchData = async () => {
    try {
      const [bizRes, citiesRes, catsRes, pkgRes] = await Promise.all([
        getBusinesses({ limit: 1000 }),
        getCities(),
        getCategories(),
        getPackages()
      ]);
      setBusinesses(bizRes.data);
      setCities(citiesRes.data);
      setCategories(catsRes.data);
      setPackages(pkgRes.data);
    } catch (error) {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredBusinesses = businesses.filter(biz => {
    const matchesSearch = biz.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || biz.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getCityName = (id) => cities.find(c => c.id === id)?.name || '-';
  const getCategoryName = (id) => categories.find(c => c.id === id)?.name || '-';
  const getPackageName = (id) => packages.find(p => p.id === id)?.name || '-';

  const handleOpenDialog = (business = null) => {
    if (business) {
      setEditingBusiness(business);
      setFormData({
        name: business.name || '',
        description: business.description || '',
        city_id: business.city_id || '',
        category_id: business.category_id || '',
        district: business.district || '',
        address: business.address || '',
        phone: business.phone || '',
        whatsapp: business.whatsapp || '',
        website: business.website || '',
        email: business.email || '',
        logo_url: business.logo_url || '',
        cover_image_url: business.cover_image_url || '',
        gallery: business.gallery || [],
        package_id: business.package_id || '',
        start_date: business.start_date?.split('T')[0] || '',
        end_date: business.end_date?.split('T')[0] || '',
        status: business.status || 'draft',
        payment_method: business.payment_method || '',
        collected_by: business.collected_by || '',
        payment_notes: business.payment_notes || '',
        seo_title: business.seo_title || '',
        seo_description: business.seo_description || ''
      });
      setGalleryText((business.gallery || []).join('\n'));
    } else {
      setEditingBusiness(null);
      setFormData({
        name: '',
        description: '',
        city_id: '',
        category_id: '',
        district: '',
        address: '',
        phone: '',
        whatsapp: '',
        website: '',
        email: '',
        logo_url: '',
        cover_image_url: '',
        gallery: [],
        package_id: '',
        start_date: '',
        end_date: '',
        status: 'draft',
        payment_method: '',
        collected_by: '',
        payment_notes: '',
        seo_title: '',
        seo_description: ''
      });
      setGalleryText('');
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const gallery = galleryText.split('\n').filter(g => g.trim());
    const data = { 
      ...formData, 
      gallery,
      start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
      end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null
    };
    
    try {
      if (editingBusiness) {
        await updateBusiness(editingBusiness.id, data);
        toast.success('İşletme güncellendi');
      } else {
        await createBusiness(data);
        toast.success('İşletme eklendi');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handleDelete = async (business) => {
    if (!window.confirm(`${business.name} işletmesini silmek istediğinize emin misiniz?`)) return;
    try {
      await deleteBusiness(business.id);
      toast.success('İşletme silindi');
      fetchData();
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  const getStatusBadge = (status) => {
    const opt = statusOptions.find(s => s.value === status);
    return (
      <Badge className={`${opt?.color} text-white`}>
        {opt?.label || status}
      </Badge>
    );
  };

  const isExpiringSoon = (endDate) => {
    if (!endDate) return false;
    const end = new Date(endDate);
    const now = new Date();
    const diff = (end - now) / (1000 * 60 * 60 * 24);
    return diff > 0 && diff <= 7;
  };

  return (
    <div className="space-y-6 page-enter" data-testid="admin-listings">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex flex-col sm:flex-row gap-4 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="İşletme ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 bg-card border-white/10"
              data-testid="listing-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48 bg-card border-white/10">
              <SelectValue placeholder="Durum filtrele" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Durumlar</SelectItem>
              {statusOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-listing-btn">
          <Plus className="w-4 h-4 mr-2" /> İşletme Ekle
        </Button>
      </div>

      {/* Table */}
      <div className="bg-card border border-white/10 rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead>İşletme</TableHead>
              <TableHead>Şehir / Kategori</TableHead>
              <TableHead>Paket</TableHead>
              <TableHead>Durum</TableHead>
              <TableHead>İstatistikler</TableHead>
              <TableHead className="w-32 text-right">İşlemler</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                </TableCell>
              </TableRow>
            ) : filteredBusinesses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  {searchQuery || statusFilter !== 'all' ? 'İşletme bulunamadı' : 'Henüz işletme eklenmemiş'}
                </TableCell>
              </TableRow>
            ) : (
              filteredBusinesses.map(biz => (
                <TableRow key={biz.id} className="border-white/10" data-testid={`listing-row-${biz.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {biz.logo_url ? (
                        <img src={biz.logo_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center font-heading font-bold text-primary">
                          {biz.name.charAt(0)}
                        </div>
                      )}
                      <div>
                        <p className="font-medium">{biz.name}</p>
                        {biz.district && (
                          <p className="text-muted-foreground text-xs">{biz.district}</p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm">{getCityName(biz.city_id)}</p>
                    <p className="text-muted-foreground text-xs">{getCategoryName(biz.category_id)}</p>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm">{getPackageName(biz.package_id)}</p>
                    {biz.end_date && (
                      <p className={`text-xs flex items-center gap-1 ${isExpiringSoon(biz.end_date) ? 'text-yellow-500' : 'text-muted-foreground'}`}>
                        {isExpiringSoon(biz.end_date) && <AlertTriangle className="w-3 h-3" />}
                        <Calendar className="w-3 h-3" />
                        {new Date(biz.end_date).toLocaleDateString('tr-TR')}
                      </p>
                    )}
                  </TableCell>
                  <TableCell>{getStatusBadge(biz.status)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Eye className="w-3 h-3" /> {biz.views || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <Phone className="w-3 h-3" /> {biz.call_clicks || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageCircle className="w-3 h-3" /> {biz.whatsapp_clicks || 0}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleOpenDialog(biz)}
                      data-testid={`edit-listing-${biz.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleDelete(biz)}
                      data-testid={`delete-listing-${biz.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingBusiness ? 'İşletme Düzenle' : 'Yeni İşletme'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="basic">Temel</TabsTrigger>
                <TabsTrigger value="contact">İletişim</TabsTrigger>
                <TabsTrigger value="package">Paket</TabsTrigger>
                <TabsTrigger value="media">Medya & SEO</TabsTrigger>
              </TabsList>
              
              <TabsContent value="basic" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="name">İşletme Adı *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="bg-secondary border-white/10"
                    data-testid="listing-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Şehir *</Label>
                    <Select 
                      value={formData.city_id} 
                      onValueChange={(v) => setFormData({ ...formData, city_id: v })}
                    >
                      <SelectTrigger className="bg-secondary border-white/10">
                        <SelectValue placeholder="Şehir seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        {cities.map(city => (
                          <SelectItem key={city.id} value={city.id}>{city.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Kategori *</Label>
                    <Select 
                      value={formData.category_id} 
                      onValueChange={(v) => setFormData({ ...formData, category_id: v })}
                    >
                      <SelectTrigger className="bg-secondary border-white/10">
                        <SelectValue placeholder="Kategori seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="district">İlçe/Semt</Label>
                  <Input
                    id="district"
                    value={formData.district}
                    onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                    className="bg-secondary border-white/10"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Açıklama</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="bg-secondary border-white/10"
                  />
                </div>
              </TabsContent>
              
              <TabsContent value="contact" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="address">Adres</Label>
                  <Textarea
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    rows={2}
                    className="bg-secondary border-white/10"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefon</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+90 5XX XXX XXXX"
                      className="bg-secondary border-white/10"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="whatsapp">WhatsApp</Label>
                    <Input
                      id="whatsapp"
                      value={formData.whatsapp}
                      onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                      placeholder="905XXXXXXXXX"
                      className="bg-secondary border-white/10"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">E-posta</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="bg-secondary border-white/10"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="website">Web Sitesi</Label>
                    <Input
                      id="website"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      placeholder="https://"
                      className="bg-secondary border-white/10"
                    />
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="package" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Paket</Label>
                  <Select 
                    value={formData.package_id} 
                    onValueChange={(v) => setFormData({ ...formData, package_id: v })}
                  >
                    <SelectTrigger className="bg-secondary border-white/10">
                      <SelectValue placeholder="Paket seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {packages.map(pkg => (
                        <SelectItem key={pkg.id} value={pkg.id}>
                          {pkg.name} - {pkg.price.toLocaleString('tr-TR')} ₺
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_date">Başlangıç Tarihi</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      className="bg-secondary border-white/10"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end_date">Bitiş Tarihi</Label>
                    <Input
                      id="end_date"
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      className="bg-secondary border-white/10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Durum</Label>
                  <Select 
                    value={formData.status} 
                    onValueChange={(v) => setFormData({ ...formData, status: v })}
                  >
                    <SelectTrigger className="bg-secondary border-white/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="border-t border-white/10 pt-4 mt-4">
                  <h4 className="font-medium mb-4">Ödeme Bilgileri</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Ödeme Yöntemi</Label>
                      <Select 
                        value={formData.payment_method} 
                        onValueChange={(v) => setFormData({ ...formData, payment_method: v })}
                      >
                        <SelectTrigger className="bg-secondary border-white/10">
                          <SelectValue placeholder="Seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          {paymentMethods.map(m => (
                            <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="collected_by">Tahsil Eden</Label>
                      <Input
                        id="collected_by"
                        value={formData.collected_by}
                        onChange={(e) => setFormData({ ...formData, collected_by: e.target.value })}
                        className="bg-secondary border-white/10"
                      />
                    </div>
                  </div>
                  <div className="space-y-2 mt-4">
                    <Label htmlFor="payment_notes">Ödeme Notları</Label>
                    <Textarea
                      id="payment_notes"
                      value={formData.payment_notes}
                      onChange={(e) => setFormData({ ...formData, payment_notes: e.target.value })}
                      rows={2}
                      className="bg-secondary border-white/10"
                    />
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="media" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="logo_url">Logo URL</Label>
                    <Input
                      id="logo_url"
                      value={formData.logo_url}
                      onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                      placeholder="https://"
                      className="bg-secondary border-white/10"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cover_image_url">Kapak Görseli URL</Label>
                    <Input
                      id="cover_image_url"
                      value={formData.cover_image_url}
                      onChange={(e) => setFormData({ ...formData, cover_image_url: e.target.value })}
                      placeholder="https://"
                      className="bg-secondary border-white/10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gallery">Galeri (her satıra bir URL)</Label>
                  <Textarea
                    id="gallery"
                    value={galleryText}
                    onChange={(e) => setGalleryText(e.target.value)}
                    rows={3}
                    className="bg-secondary border-white/10"
                  />
                </div>
                <div className="border-t border-white/10 pt-4 mt-4">
                  <h4 className="font-medium mb-4">SEO</h4>
                  <div className="space-y-2">
                    <Label htmlFor="seo_title">SEO Başlık</Label>
                    <Input
                      id="seo_title"
                      value={formData.seo_title}
                      onChange={(e) => setFormData({ ...formData, seo_title: e.target.value })}
                      className="bg-secondary border-white/10"
                    />
                  </div>
                  <div className="space-y-2 mt-4">
                    <Label htmlFor="seo_description">SEO Açıklama</Label>
                    <Textarea
                      id="seo_description"
                      value={formData.seo_description}
                      onChange={(e) => setFormData({ ...formData, seo_description: e.target.value })}
                      rows={2}
                      className="bg-secondary border-white/10"
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>
            
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                İptal
              </Button>
              <Button type="submit" data-testid="save-listing-btn">
                {editingBusiness ? 'Güncelle' : 'Ekle'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
